import { cn } from "@/utils/cn";
import Heading from "@tiptap/extension-heading";
import { Link } from "@tiptap/extension-link";
import Paragraph from "@tiptap/extension-paragraph";
import { EditorContent, Mark, mergeAttributes, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Minus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  FaBold,
  FaEraser,
  FaItalic,
  FaLink,
  FaListOl,
  FaListUl,
  FaRedo,
  FaStrikethrough,
  FaUndo,
} from "react-icons/fa";

const FontWeight = Mark.create({
  name: "fontWeight",

  addAttributes() {
    return {
      weight: {
        default: "normal",
        parseHTML: (element) => element.style.fontWeight || "normal",
        renderHTML: (attributes) => {
          return { style: `font-weight: ${attributes.weight};` };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[style*=font-weight]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setFontWeight:
        (weight) =>
        ({ chain }) => {
          return chain().setMark("fontWeight", { weight }).run();
        },
      unsetFontWeight:
        () =>
        ({ chain }) => {
          return chain().unsetMark("fontWeight").run();
        },
    };
  },
});

const RichTextEditor = ({
  formik,
  fieldName,
  saveError,
  label = "Content",
  placeholder = "Start typing...",
  enableCharCounter = true,
  enableWordCounter = true,
  onChange = null,
  ...props
}) => {
  // useRef
  const editorRef = useRef({
    content: "",
    node: null,
  });

  // useState
  const [counter, setCounter] = useState({ char: 0, word: 0 });

  // Extend the Heading extension with custom TailwindCSS classes
  const CustomHeading = Heading.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        class: {
          default: "",
          parseHTML: (element) => element.getAttribute("class"),
          renderHTML: (attributes) => {
            const { level } = attributes;
            const classes = {
              1: "text-2xl font-bold mb-3",
              2: "text-xl font-semibold mb-3",
              3: "text-lg font-medium mb-3",
              4: "text-base font-bold mb-3",
            };
            return {
              class: classes[level] || "",
            };
          },
        },
      };
    },
  });

  // Extend the Paragraph extension with custom TailwindCSS classes
  const CustomParagraph = Paragraph.extend({
    addAttributes() {
      return {
        class: {
          default: "text-base leading-relaxed mb-2 font-light min-h-6",
          parseHTML: (element) => element.getAttribute("class"),
          renderHTML: (attributes) => {
            return {
              class: attributes.class,
            };
          },
        },
      };
    },
  });

  // Initialize the editor
  const editor = useEditor({
    editorProps: {
      attributes: {
        class: "focus:outline-none", // Add any base classes for the editor here
      },
    },
    extensions: [
      StarterKit.configure({
        bold: {
          HTMLAttributes: {
            class: "font-bold",
          },
        },
        heading: false, // Disable the default heading
        paragraph: false,
        bulletList: {
          HTMLAttributes: {
            class: "list-disc px-4 mb-3",
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: "list-decimal px-4 font-light mb-3",
          },
        },
        listItem: {
          HTMLAttributes: {
            class: "[&>p]:!mb-0",
          },
        },
      }),
      FontWeight,
      CustomHeading.configure({ levels: [1, 2, 3, 4] }), // Use the custom heading
      CustomParagraph,
      Link.configure({
        autolink: true, // Automatically create links from URLs
        linkOnPaste: true, // Automatically detect and set links when pasted
        HTMLAttributes: {
          class: "text-blue-500 underline hover:text-blue-700",
        },
      }),
    ],
    onUpdate: ({ editor }) => {
      if (enableCharCounter) updateCharCount(editor);
      if (enableWordCounter) updateWordCount(editor);

      const newContent = editor.getHTML();

      // Update formik or external state only if content has changed
      if (editorRef.current.content !== newContent) {
        editorRef.current.content = newContent;

        if (onChange) {
          onChange(editor);
        } else {
          formik.setFieldValue(fieldName, newContent);
        }
      }
    },
  });
  const updateCharCount = useCallback((editor) => {
    let text = editor.getText();
    const normalizedText = text.replace(/[\r\n]/g, "");
    setCounter((prevCounter) => ({
      ...prevCounter,
      char: normalizedText.length,
    }));
  }, []);
  const updateWordCount = useCallback((editor) => {
    let text = editor.getText();
    const words = text.trim().split(/\s+/);
    setCounter((prevCounter) => ({
      ...prevCounter,
      word: words.filter(Boolean).length,
    }));
  }, []);
  useEffect(() => {
    if (editor && formik?.values[fieldName] !== editorRef.current.content) {
      editor.commands.setContent(formik?.values[fieldName]);
      if (enableCharCounter) updateCharCount(editor);
      if (enableWordCounter) updateWordCount(editor);
    }
  }, [
    editor,
    enableCharCounter,
    enableWordCounter,
    fieldName,
    formik?.values,
    updateCharCount,
    updateWordCount,
  ]);

  if (!editor) {
    return null;
  }

  // Command handler for editor actions
  const handleCommand = (command, options = null) => {
    const chain = editor.chain().focus();

    if (command === "setFontWeight") {
      if (editor.isActive("bold")) chain.toggleBold(); // Unset bold if fontWeight is applied
      if (editor.isActive("fontWeight", { weight: options })) {
        chain.unsetMark("fontWeight").run();
        return;
      }
      chain.setMark("fontWeight", { weight: options }).run();
      return;
    }

    if (command === "toggleBold") {
      if (editor.isActive("fontWeight")) chain.unsetMark("fontWeight");
      chain.toggleBold().run();
      return;
    }

    if (options) {
      chain[command](options).run();
    } else {
      chain[command]().run();
    }
  };

  // Toolbar button component
  const Button = ({ onClick, isActive, children, ...props }) => (
    <button
      className={cn(
        "rounded-[7px] border-[#595959] text-jersium-black p-2 flex items-center justify-center",
        isActive && "bg-gray-200"
      )}
      onMouseDown={(e) => {
        e.preventDefault(); // Prevent default focus on button
        onClick();
      }}
      {...props}
    >
      {children}
    </button>
  );

  const insertLink = () => {
    const url = prompt("Enter the URL:");
    if (url) {
      editor
        .chain()
        .focus()
        .extendMarkRange("link") // Ensure the link applies to the whole word/selection
        .setLink({ href: url })
        .run();
    }
  };

  // Get the error message
  const errorMessage =
    formik?.errors[fieldName] ??
    saveError?.response?.data?.errors?.[fieldName]?.join(" ");

  // Handle scrolling behavior inside and outside the editor
  const handleEditorScroll = (event) => {
    const container = editorRef.current.node;
    if (!container) return;

    const atTop = container.scrollTop === 0;
    const atBottom =
      container.scrollTop + container.clientHeight >= container.scrollHeight;

    if (atTop && event.deltaY < 0) {
      // When at the top of the editor, allow scrolling the page up
      window.scrollBy({ top: -50, behavior: "smooth" });
      return;
    }

    if (atBottom && event.deltaY > 0) {
      // When at the bottom of the editor, allow scrolling the page down
      window.scrollBy({ top: 50, behavior: "smooth" });
      return;
    }

    // Stop the event from bubbling up when scrolling within the editor bounds
    event.stopPropagation();
  };

  return (
    <>
      {label && (
        <label className="mb-2">
          <span className="mr-1 text-red-500">*</span>
          {label}
        </label>
      )}
      <div className="editor-container w-full border rounded-md p-2">
        {/* Toolbar */}
        <div className="toolbar flex gap-2 mb-2 border-b pb-2">
          {/* Text Formatting */}
          <div className="flex gap-2 border-r-2 pr-2">
            <Button
              onClick={() => handleCommand("toggleBold")}
              title="Bold"
              isActive={editor.isActive("bold")}
            >
              <FaBold />
            </Button>
            <Button
              onClick={() => handleCommand("toggleItalic")}
              title="Italic"
              isActive={editor.isActive("italic")}
            >
              <FaItalic />
            </Button>
            <Button
              onClick={() => handleCommand("toggleStrike")}
              title="Strikethrough"
              isActive={editor.isActive("strike")}
            >
              <FaStrikethrough />
            </Button>
            <Button
              onClick={() => handleCommand("setFontWeight", "200")}
              title="Extra Light"
              isActive={editor.isActive("fontWeight", { weight: "200" })}
            >
              <span className="font-extralight">Extra Light</span>
            </Button>
            <Button
              onClick={() => handleCommand("setFontWeight", "400")}
              title="Regular"
              isActive={editor.isActive("fontWeight", { weight: "400" })}
            >
              <span className="font-normal">Regular</span>
            </Button>
            <Button
              onClick={() => handleCommand("setFontWeight", "600")}
              title="Semibold"
              isActive={editor.isActive("fontWeight", { weight: "600" })}
            >
              <span className="font-semibold">Semibold</span>
            </Button>
          </div>

          {/* Headings */}
          <div className="flex gap-2 border-r-2 pr-2">
            {[1, 2, 3, 4].map((level) => (
              <Button
                key={level}
                onClick={() => handleCommand("toggleHeading", { level })}
                isActive={editor.isActive("heading", { level })}
                title={`Heading ${level}`}
              >
                H{level}
              </Button>
            ))}
          </div>

          <div className="flex gap-2 border-r-2 pr-2">
            {/* Lists */}
            <Button
              onClick={() => handleCommand("toggleBulletList")}
              title="Bullet List"
              isActive={editor.isActive("bulletList")}
            >
              <FaListUl />
            </Button>
            <Button
              onClick={() => handleCommand("toggleOrderedList")}
              title="Numbered List"
              isActive={editor.isActive("orderedList")}
            >
              <FaListOl />
            </Button>

            {/* Links */}
            <Button onClick={insertLink} title="Link">
              <FaLink />
            </Button>

            {/* HR */}
            <Button
              onClick={() =>
                editor.chain().focus().insertContent("<hr />").run()
              }
              title="HR (Horizontal Rule)"
            >
              <Minus />
            </Button>
          </div>

          <div className="flex gap-2 pr-2">
            {/* Undo/Redo */}
            <Button onClick={() => handleCommand("undo")} title="Undo">
              <FaUndo />
            </Button>
            <Button onClick={() => handleCommand("redo")} title="Redo">
              <FaRedo />
            </Button>

            {/* Clear Formatting */}
            <Button
              onClick={() => {
                handleCommand("clearNodes");
                handleCommand("unsetAllMarks");
              }}
              title="Clear Formatting"
            >
              <FaEraser />
            </Button>
          </div>
        </div>

        {/* Editor Content */}
        <div
          className="relative min-h-[150px] max-h-[500px] overflow-y-auto"
          onWheel={handleEditorScroll}
          ref={(node) => {
            editorRef.current.node = node;
          }}
        >
          <EditorContent
            editor={editor}
            className="editor-content"
            {...props}
          />
          {/* Placeholder */}
          <div
            className={cn(
              "absolute inset-0 pointer-events-none text-gray-400 px-1 py-0",
              editor.isEmpty && "block",
              !editor.isEmpty && "hidden"
            )}
          >
            {placeholder}
          </div>
          <div className="mt-1 text-info-gray">
            {enableCharCounter && <div>Character Count: {counter.char}</div>}
            {enableWordCounter && <div>Word Count: {counter.word}</div>}
          </div>
        </div>
      </div>
      {((formik.touched[fieldName] && formik.errors[fieldName]) ||
        saveError?.status === 422) && (
        <div className="text-red-500 mt-2">
          {errorMessage && errorMessage + "*"}
        </div>
      )}
    </>
  );
};

export default RichTextEditor;
